import { Trans } from '@lingui/react/macro';
import dayjs from 'dayjs';
import { useState } from 'react';

import {
  type AudienceActivationType,
  AudienceJobStatus,
  type AudienceJobType,
  type AudienceType,
} from '@/api/audience';
import { Button } from '@/components/buttons';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/Dialog';
import { Link, Text } from '@/components/typography';
import { USDateViewFormat } from '@/constants';
import { numberFormat } from '@/helper/numberFormatter';
import { ROUTES } from '@/router';

import { getActivationAudienceType } from './audience-activation-util';

type Props = {
  audience?: AudienceType;
  activation?: AudienceActivationType;
  onClose: () => void;
  onDeactivate?: () => void;
};

const getLastActivationJobStatus = (lastActivationJob: AudienceJobType | undefined) => {
  switch (lastActivationJob?.status) {
    case AudienceJobStatus.COMPLETED:
      return <Trans>Successful</Trans>;
    case AudienceJobStatus.ERROR:
      return <Trans>Error</Trans>;
    case AudienceJobStatus.PROCESSING:
      return <Trans>Processing</Trans>;
    default:
      return <Trans>Unknown</Trans>;
  }
};

const AudienceActivationDetailDialog = ({ audience, activation, onClose, onDeactivate }: Props) => {
  const [isDeleting, setIsDeleting] = useState(false);

  const audienceType = activation ? getActivationAudienceType(activation.provider) : undefined;

  const handleDeactivate = async () => {
    setIsDeleting(true);
    await onDeactivate?.();
    setIsDeleting(false);
    onClose();
  };

  const handleOpenChange = (isOpen: boolean) => {
    if (isDeleting) return;
    if (!isOpen) onClose();
  };

  return (
    <Dialog open={activation != null && audience != null} onOpenChange={handleOpenChange}>
      <DialogContent showCloseButton={!isDeleting}>
        <DialogHeader>
          <DialogTitle>
            {activation?.provider.name ?? <Trans>Unknown Vendor</Trans>}{' '}
            <Trans>Activation Details</Trans>
          </DialogTitle>
        </DialogHeader>
        <div className="flex flex-col gap-8">
          <div className="flex flex-col gap-1">
            <div className="flex">
              <strong>
                <Trans>Activation Date:</Trans>
              </strong>
              {activation?.activatedAt ? (
                dayjs(activation.activatedAt).format(USDateViewFormat)
              ) : (
                <Trans>Unknown</Trans>
              )}
            </div>
            <div className="flex">
              <strong>
                <Trans>Activated by:</Trans>
              </strong>
              {activation?.activatedBy ? (
                <Link variant="body2" to={ROUTES.userById(activation.activatedBy.id)}>
                  {activation.activatedBy.name} ({activation.activatedBy.email})
                </Link>
              ) : (
                <Trans>Unknown</Trans>
              )}
            </div>
            <div className="flex">
              <strong>
                <Trans>Last Sync Attempt:</Trans>
              </strong>
              {activation?.lastActivationJob?.created_at ? (
                dayjs(activation.lastActivationJob.created_at).format(USDateViewFormat)
              ) : (
                <Trans>Unknown</Trans>
              )}
            </div>
            <div className="flex">
              <strong>
                <Trans>Status:</Trans>
              </strong>
              {getLastActivationJobStatus(activation?.lastActivationJob)}
            </div>
            <div className="flex">
              <strong>
                {audienceType === 'email' && <Trans>Emails:</Trans>}
                {audienceType === 'domain' && <Trans>Domains:</Trans>}
                {audienceType === 'ip' && <Trans>IPs:</Trans>}
              </strong>
              {audienceType === 'email' && numberFormat(audience?.metrics.emails)}
              {audienceType === 'domain' && numberFormat(audience?.metrics.members)}
              {audienceType === 'ip' && numberFormat(audience?.metrics.ips)}
            </div>
          </div>
          {activation?.lastActivationJob?.errors && (
            <Text variant="body2" color="red">
              {activation?.lastActivationJob?.errors}
            </Text>
          )}
        </div>
        <DialogFooter>
          <DialogClose
            render={
              <Button size="large" disabled={isDeleting}>
                <Trans>Close</Trans>
              </Button>
            }
          />
          <Button
            size="large"
            variant="primary"
            danger
            isLoading={isDeleting}
            onClick={handleDeactivate}
          >
            <Trans>Deactivate</Trans>
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default AudienceActivationDetailDialog;
